(ns ui.view.kanban-opensearch.model
  "The state of one Kanban board, and how the board loads its cards. The state is under
  `[::state kanban-id]`. The map below describes it.

  A row is a swimlane. A board with no rows has one row, `nil`. A cell is a row and a
  column. The cards of a cell are in `:cards-by-column`, under the column, or under
  `[row column]` on a board with rows.

  How the cards load:
  - The columns load first. The rows load next, when the board has rows.
  - Each strip of columns reports the columns that it shows, with its row
    (`::set-visible-columns`). The list of rows reports the rows that are in or near
    view (`::set-visible-rows`). Both reports go into `:viewport`.
  - A cell loads its first page of cards when it is in view and nobody has asked for it
    yet. `:requested-cells` holds the cells that were asked for, so a cell loads one time.
  - A column that comes into view in one row does not load for the other rows.
  - An empty row is a header. It has no strip and loads nothing.
  - The set of active columns of a strip only grows. A report that is a little narrower
    than the one before it loads nothing. Reports that alternated sent more than 100000
    requests (DEV-8218).
  - A reload (`::reload-visible-columns`) loads again only the cells in view, for the
    columns that each strip shows. `:reloading-values` holds those columns until their
    cards arrive or fail.")

[:map
 ;; The persisted config you loaded (or edited) for this kanban.
 ;; Keep a copy here so the view can render without reaching elsewhere.
 [:config [:any]]                                           ;; validate with config-model/model in code

 [:compiled-config
  [:map
   [:card-config [:any]]
   [:fields [:vector any?]]]]

 ;; When config changes, bump this to invalidate stale in-flight requests.
 [:rev int?]

 ;; Column discovery state (facet-based).
 [:columns
  [:map
   [:status [:enum :idle :loading :loaded :error]]
   [:error {:optional true} any?]
   ;; Ordered list of column keys (facet values) as returned from backend,
   ;; already merged with pinned values, ordering, and max-columns.
   [:order [:vector any?]]

   ;; Map of value -> column meta (label/count). Value must be the facet value.
   [:by-value
    [:map-of
     any?
     [:map
      [:value any?]
      [:label {:optional true} string?]
      [:count {:optional true} int?]
      ;; Whether this column is currently visible (runtime computed).
      [:visible? {:optional true} boolean?]]]]]]

 [:row-groups
  [:map
   [:status [:enum :idle :loading :loaded :error]]
   [:error {:optional true} any?]
   [:order [:vector any?]]
   [:by-value
    [:map-of
     any?
     [:map
      [:value any?]
      [:label {:optional true} string?]
      [:count {:optional true} int?]]]]]]

 ;; Tracks which swimlanes and columns are in or near the viewport (computed by UI).
 ;; Only the cells (swimlane + column) that are in view load their cards.
 [:viewport
  [:map
   ;; Swimlane values that are in or near view (vertical). Empty when the board has no swimlanes.
   [:visible-rows [:set any?]]
   ;; The columns of each swimlane strip, by swimlane value. A board with no swimlanes has one
   ;; strip, with the swimlane value nil.
   [:rows
    [:map-of
     any?
     [:map
      ;; Visible column values (facet keys) of the strip.
      [:visible-values [:set any?]]
      ;; "Buffer" column values (e.g. +/- N columns) of the strip.
      [:buffer-values [:set any?]]
      ;; Union of all the visible + buffer values that the strip has reported. It only grows.
      [:active-values [:set any?]]]]]
   ;; The `[swimlane column]` cells that were asked for. A cell is asked for once, unless a reload.
   [:requested-cells [:set any?]]
   ;; Column values that are loading again after a reload.
   [:reloading-values [:set any?]]]]

 ;; Per-column card data + paging state.
 [:cards-by-column
  [:map-of
   any?
   [:map
    [:status [:enum :idle :loading :loaded :error]]
    [:error {:optional true} any?]

    ;; The cards currently rendered/held for that column.
    [:items [:vector any?]]

    ;; Cursor for next page (opaque), or nil if not loaded yet.
    ;; Store exactly what backend returns.
    [:next-cursor {:optional true} any?]

    ;; Total hits for this column query, if backend provides it.
    [:total {:optional true} int?]

    ;; Whether backend indicates there's more to load.
    [:has-more? boolean?]

    ;; Used to drop stale responses when config/rev changes.
    [:rev int?]

    ;; Optional: track in-flight request id to ignore duplicates.
    [:request-id {:optional true} int?]]]]

 ;; Drag & drop / mutation staging
 [:dnd
  [:map
   [:status [:enum :idle :dragging :saving :error]]
   [:error {:optional true} any?]
   ;; Last move request (for retry/debug)
   [:last-move {:optional true} [:map
                                 [:card-id any?]
                                 [:from any?]
                                 [:to any?]]]]]

 ;; If you want a global search box like grid has:
 [:search
  [:map
   [:text {:optional true} string?]
   ;; debounce token/timer id stored by effect handler
   [:debounce-id {:optional true} any?]]]]

(def root-path [::state])

(def request-seq-path
  "The id of the last request of the columns of any board. It is outside the
   state of the boards, so a board that closes and opens again does not start the
   ids again."
  [::request-seq])

(def config-key :config)
(def compiled-config-key :compiled-config)

(def errors-key :errors)
(def rev-key :rev)
;; NOTE: keep this in sync with the spec key :cards-by-column
(def columns-key :columns)
(def row-groups-key :row-groups)
(def viewport-key :viewport)
(def cards-by-column-key :cards-by-column)
(def dnd-key :dnd)
(def text-search-key :search)

(defn kanban-path
  [kanban-id]
  (into root-path [kanban-id]))

(defn config-path
  [kanban-id]
  (into root-path [kanban-id config-key]))

(defn compiled-config-path
  [kanban-id]
  (into root-path [kanban-id compiled-config-key]))

(defn rev-path
  [kanban-id]
  (into root-path [kanban-id rev-key]))

(defn columns-path
  [kanban-id]
  (into root-path [kanban-id columns-key]))

(defn row-groups-path
  [kanban-id]
  (into root-path [kanban-id row-groups-key]))

(defn viewport-path
  [kanban-id]
  (into root-path [kanban-id viewport-key]))

(defn cards-by-column-path
  [kanban-id]
  (into root-path [kanban-id cards-by-column-key]))

(defn dnd-path
  [kanban-id]
  (into root-path [kanban-id dnd-key]))

(defn text-search-path
  [kanban-id]
  (into root-path [kanban-id text-search-key]))

(defn errors-path
  [kanban-id]
  (into root-path [kanban-id errors-key]))

(defn error-path
  [kanban-id k]
  (into root-path [kanban-id errors-key k]))

(defn row-group-empty?
  "True when the count of a swimlane says that it has no cards. A swimlane with no
  known count is not empty. An empty swimlane is shown as a header, and its cards are
  not loaded."
  [row-meta]
  (let [row-count (:count row-meta)]
    (boolean (and (number? row-count)
                  (zero? row-count)))))

(def row-group-height
  "The height in px of a swimlane with cards in the list of swimlanes, with its bottom
  padding. It is measured in the browser: the section has a minimum height of 360 px
  (`tw2-min-h-[360px]`), and the wrapper of the list item has 16 px of padding
  (`tw2-pb-4`). Keep it in line with `kanban-row-group` in the view."
  376)

(def empty-row-group-height
  "The height in px of an empty swimlane, a header only, with its bottom padding. It is
  measured in the browser: 35.5 px for the section and 16 px of padding."
  52)

(defn row-group-height-estimates
  "The estimated height in px of each swimlane of `ordered-row-groups`, in the same
  order. The list of swimlanes uses it to give a scroll height that is close to the
  real one before the swimlanes are measured. Without it, each swimlane that is not
  measured counts as a swimlane with cards, and the list is too high when swimlanes are
  empty, and the scroll position jumps while the list is measured."
  [{:keys [ordered-row-groups row-groups-lookup]}]
  (mapv (fn [row-value]
          (if (row-group-empty? (get row-groups-lookup row-value))
            empty-row-group-height
            row-group-height))
        ordered-row-groups))

(defn strip-start-index
  "The index of the column that a strip shows first when it is mounted again. `start`
  is the index that the strip reported last, or nil when it reported none. It is kept
  inside the columns that the strip has now."
  [{:keys [start column-count]}]
  (-> (or start 0)
      (max 0)
      (min (max 0 (dec (or column-count 0))))))

(defn strip-start-write
  "Decide how a strip handles a first-column index that it reports. A strip that is
  mounted again reports index 0 for a moment, before it restores its saved column.
  That transient 0 must not overwrite a saved column. `saved` is the saved column, already
  clamped to the columns that the strip has now, so a strip whose restore target is 0 does
  not keep an old column. `confirmed?` is false until the strip has restored its scroll.
  Return the value to save and the next `confirmed?`.

  - When the strip is confirmed, every report is saved.
  - Before that, a report of 0 while a later column is saved is the transient. Keep the
    saved value, and stay unconfirmed.
  - Any other report is the restore that landed, or a real scroll. Save it, and confirm
    the strip, so a later scroll back to the first column is saved too."
  [{:keys [saved incoming confirmed?]}]
  (let [saved (or saved 0)]
    (if (and (not confirmed?)
             (zero? incoming)
             (pos? saved))
      {:value saved :confirmed? false}
      {:value incoming :confirmed? true})))

(defn strip-start-next
  "Decide the column that a strip keeps, and whether it is confirmed, after it reports its
  first column. `saved` is the column that the strip reported last (raw, or nil). It is
  clamped to the columns that the strip has now, so a strip that lost columns does not keep
  a column that it cannot show, and then `strip-start-write` guards the transient 0 of a
  remount."
  [{:keys [saved column-count incoming confirmed?]}]
  (strip-start-write {:saved      (strip-start-index {:start saved :column-count column-count})
                      :incoming   incoming
                      :confirmed? confirmed?}))
